/**
 * KaiLux — Admin Board: Add-on Pricing Management (Phase F2)
 * Route: /admin → Plans → Add-ons tab (rendered inside PlanManagement.jsx)
 *
 * Reads and writes platform_config.addons via the generic admin config
 * endpoints (GET/PUT /api/admin/config/{key}).
 * Changes take effect immediately — no redeploy needed.
 *
 * NOTE: the old Starter/Professional/Enterprise feature-tier system (and the
 * "included in plan" checkboxes that used to live here) was removed when
 * KaiLux moved to per-seat pricing (see PRICING.md) — every user gets the
 * full platform regardless of seat count, and add-ons are simply active/
 * priced or not. Don't reintroduce a plan-tier concept here.
 */

import { useState, useEffect } from "react";
import { adminGetConfig, adminUpdateConfig } from "../api/kailux";
import Icon from './Icon'

function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors
        ${checked ? "bg-black" : "bg-gray-300"} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      <span
        className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform
          ${checked ? "translate-x-4" : "translate-x-0"}`}
      />
    </button>
  );
}

function CurrencyInput({ value, onChange, disabled }) {
  return (
    <div className="relative w-28">
      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-400 select-none">R</span>
      <input
        type="number"
        min="0"
        step="50"
        value={value}
        onChange={e => onChange(parseFloat(e.target.value) || 0)}
        disabled={disabled}
        className="w-full pl-6 pr-2 py-1.5 text-sm text-right border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-black disabled:bg-gray-50 disabled:text-gray-400"
      />
    </div>
  );
}

export default function AddonPricing() {
  const [addons, setAddons] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    adminGetConfig("addons")
      .then(res => {
        setAddons(res.config_value || {});
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load add-on configuration.");
        setLoading(false);
      });
  }, []);

  function updateAddon(key, field, value) {
    setAddons(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: value }
    }));
    setDirty(true);
    setSaved(false);
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      await adminUpdateConfig("addons", addons);
      setSaved(true);
      setDirty(false);
    } catch {
      setError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="p-8 text-sm text-gray-400">Loading add-on configuration…</div>;
  if (error && !addons) return <div className="p-8 text-sm text-red-600">{error}</div>;

  const addonEntries = Object.entries(addons || {});

  return (
    <div>
      <div className="flex items-center justify-between mb-6 mt-4">
        <div>
          <h3 className="text-base text-gray-900" style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800 }}>Add-on Pricing</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Changes take effect immediately — no redeploy needed.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {saved && <span className="text-xs text-green-600 font-medium"><Icon name="check" /> Saved</span>}
          {dirty && !saved && <span className="text-xs text-amber-600">Unsaved changes</span>}
          <button
            onClick={handleSave}
            disabled={saving || !dirty}
            className="px-4 py-2 bg-black text-white text-sm font-medium rounded hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="space-y-4">
        {addonEntries.map(([key, addon]) => (
          <div key={key} className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <h3 className="text-sm font-semibold text-gray-900">{addon.label}</h3>
                  <Toggle
                    checked={addon.active !== false}
                    onChange={val => updateAddon(key, "active", val)}
                  />
                  <span className="text-xs text-gray-400">
                    {addon.active !== false ? "Active" : "Disabled"}
                  </span>
                </div>
                <p className="text-xs text-gray-500">{addon.description}</p>
              </div>
            </div>

            <div className="mt-4">
              {/* Pricing */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Pricing</p>
                {addon.price_per_client_monthly !== undefined && (
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-700 w-48">Per client / month:</span>
                    <CurrencyInput
                      value={addon.price_per_client_monthly}
                      onChange={val => updateAddon(key, "price_per_client_monthly", val)}
                    />
                    <span className="text-xs text-gray-400">/mo</span>
                  </div>
                )}
                {addon.price_per_report !== undefined && (
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-sm text-gray-700 w-48">Per report:</span>
                    <CurrencyInput
                      value={addon.price_per_report}
                      onChange={val => updateAddon(key, "price_per_report", val)}
                    />
                  </div>
                )}
                {addon.price_per_export !== undefined && (
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-sm text-gray-700 w-48">Per export:</span>
                    <CurrencyInput
                      value={addon.price_per_export}
                      onChange={val => updateAddon(key, "price_per_export", val)}
                    />
                  </div>
                )}
                {addon.price_per_client_monthly === 0 && addon.price_per_report === undefined && addon.price_per_export === undefined && (
                  <span className="text-xs text-green-600 font-medium">Included — no charge</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
